// The free MIT grid: the CRM tables use no Premium-only features, so they need no MUI X license.
import { DataGrid, GridPaginationModel, GridSortModel } from '@mui/x-data-grid'
import { Box, createTheme, ThemeProvider } from '@mui/material'
import { heIL } from '@mui/material/locale'
import columnsConfig, {
  CrmGridRow,
  CrmRowChangeHandler,
} from '@/components/features/config/columns/consumers/column.crm.config'
import { useAppTheme } from '@/theme/ThemeContext'
import { GRID_LOCALE, GRID_LOCALE_TEXT } from '@/theme'
import { useMemo } from 'react'
import { gridLoadingDimSx, gridLoadingSlotProps } from '../gridLoading'
import type { APICrmReference } from '@/services/crmService'

type ServerPaging = {
  rowCount: number
  paginationModel: GridPaginationModel
  onPaginationModelChange: (model: GridPaginationModel) => void
  sortModel: GridSortModel
  onSortModelChange: (model: GridSortModel) => void
}

export default function CRMDataGrid({
  rows,
  reference,
  onRowChange,
  loading = false,
  draft = false,
  meterLocked = false,
  paging,
}: {
  rows: CrmGridRow[]
  reference?: APICrmReference
  onRowChange?: CrmRowChangeHandler
  loading?: boolean
  /** The task modal's single editable row. */
  draft?: boolean
  meterLocked?: boolean
  /** Server-side pagination and sorting for list sections. */
  paging?: ServerPaging
}) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const BG_COLOR = isDarkMode ? '#202B40' : '#FAFBFD'
  const HEADER_BG = isDarkMode ? '#202B40 !important' : '#FAFBFD !important'
  const TEXT_COLOR = isDarkMode ? '#FFFFFF' : '#8F98A5'

  const theme = useMemo(
    () =>
      createTheme(
        {
          direction: 'rtl',
          palette: {
            mode: mode,
          },
          typography: {
            fontFamily: '"Rubik", "Helvetica", "Arial", sans-serif',
          },
        },
        heIL,
        GRID_LOCALE
      ),
    [mode]
  )

  const columns = useMemo(
    () =>
      columnsConfig({ reference, onRowChange, isDarkMode, draft, meterLocked }),
    [reference, onRowChange, isDarkMode, draft, meterLocked]
  )

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%', bgcolor: BG_COLOR }} dir="rtl">
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          slotProps={gridLoadingSlotProps(rows.length > 0)}
          localeText={{
            ...GRID_LOCALE_TEXT,
            noRowsLabel: 'אין נתונים',
            noResultsOverlayLabel: 'לא נמצאו תוצאות',
          }}
          checkboxSelection={false}
          disableRowSelectionOnClick
          disableColumnFilter
          {...(paging
            ? {
                pagination: true,
                paginationMode: 'server' as const,
                sortingMode: 'server' as const,
                rowCount: paging.rowCount,
                paginationModel: paging.paginationModel,
                onPaginationModelChange: paging.onPaginationModelChange,
                sortModel: paging.sortModel,
                onSortModelChange: paging.onSortModelChange,
                pageSizeOptions: [5, 10, 25, 50, 100],
              }
            : { hideFooter: true, disableColumnSorting: true })}
          sx={{
            ...gridLoadingDimSx(loading, rows.length > 0),
            width: '100%',
            backgroundColor: BG_COLOR,
            border: 'none',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: HEADER_BG,
              border: 'none !important',
              padding: '0 10px !important',
              /* rubik 14 bold */
              fontFamily: 'Rubik',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '700',
              lineHeight: '100%',
              color: TEXT_COLOR,
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              padding: '0 4px',
            },
            '& .MuiDataGrid-filler ': {
              border: 'none !important',
              backgroundColor: HEADER_BG,
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: HEADER_BG,
              fontWeight: 'bold',
              direction: 'rtl',
            },
            '& .MuiDataGrid-cell': {
              direction: 'rtl',
              color: isDarkMode ? '#FFFFFF' : '#003F61', // Row text color
            },
            '& .MuiDataGrid-virtualScroller': {
              scrollbarGutter: 'stable both-edges',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              direction: 'rtl',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              direction: 'rtl',
            },
            '& .MuiDataGrid-columnSeparator': {
              right: 'auto',
              left: 0,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: isDarkMode ? '#2d4a7c' : '#E3F5FF',
            },
          }}
        />
      </Box>
    </ThemeProvider>
  )
}
